export interface SentimentColors {
  Positive: string;
  Neutral: string;
  Negative: string;
}

export interface SocialColors {
  Facebook: string;
  Pinterest: string;
  Twitter: string;
}

export interface DefaultOptions {
  fontFace: string;
  fontColor: string;
  color: string;
  accentColor: string;
  lightGrayColor: string;
  chartColors: string[];
  sentimentColors: SentimentColors;
  socialColors: SocialColors;
}

export interface LineChartSettings {
  showTitle: boolean;
  titleFontSize: number;
  titleFontFace: string;
  titleColor: string;
  titlePos: { x: number; y: number };
  showLegend: boolean;
  legendPos: string;
  legendFontSize: number;
  legendColor: string;
  showValAxisTitle: boolean;
  valAxisLabelColor: string;
  valAxisLabelFontSize: number;
  valAxisLineShow: boolean;
  valGridLine: { size: number; color: string };
  showLeaderLines: boolean;
  valAxisTitleColor: string;
  catAxisLabelFontSize: number;
  catAxisLabelColor: string;
  catAxisLineShow: boolean;
  chartColors: string[];
  lineSize: number;
  lineDataSymbol: string;
}

export interface DoughnutChartSettings {
  showTitle: boolean;
  showPercent: boolean;
  showLegend: boolean;
  chartColors: string[];
  legendPos: string;
  legendFontSize: number;
  dataLabelColor: string;
  dataLabelFontSize: number;
  titleFontSize: number;
  titleFontFace: string;
  titleColor: string;
  titlePos: { x: number; y: number };
}

export interface PresentationSettings {
  subtitleFontSize: number;
}

export interface ChartOptions {
  x: number;
  y: number;
  w: number;
  h: number;
  legendPos?: string;
  dateRangeW?: number;
  chartOnly?: boolean;
  showLegend?: boolean;
  showValAxisTitle?: boolean;
  title?: string;
  isExecutive?: boolean;
}

export const DEFAULT_CHART_COLORS: string[] = [
  "01827e", // Onclusive teal (primary)
  "3b5998", // Blue
  "e6492d", // Red
  "f5a623", // Orange
  "7b68ee", // Purple
  "2ecc71", // Green
  "9b59b6", // Violet
  "1abc9c", // Turquoise
  "e74c3c", // Bright red
  "3498db", // Light blue
];

export const SENTIMENT_COLORS: SentimentColors = {
  Positive: "8fd1af", // Green
  Neutral: "808080", // Gray
  Negative: "b27676", // Red/pink
};

// Twitter amplification deprecated in 360; kept for backward compatibility
export const SOCIAL_COLORS: SocialColors = {
  Facebook: "3b5998",
  Pinterest: "c8232c",
  Twitter: "1DA1F2",
};

export function createDefaultOptions(chartColors?: string[]): DefaultOptions {
  const colors =
    chartColors && chartColors.length > 0
      ? chartColors.map((c) => c.replace("#", ""))
      : DEFAULT_CHART_COLORS;

  return {
    fontFace: "Helvetica Neue",
    fontColor: "363636",
    color: "363636",
    accentColor: "01827e",
    lightGrayColor: "636569",
    chartColors: colors,
    sentimentColors: SENTIMENT_COLORS,
    socialColors: SOCIAL_COLORS,
  };
}

export const DEFAULT_OPTIONS: DefaultOptions = createDefaultOptions();

export function createLineChartSettings(
  defaultOptions: DefaultOptions,
): LineChartSettings {
  return {
    showTitle: false,
    titleFontSize: 12,
    titleFontFace: "Helvetica Neue Bold",
    titleColor: defaultOptions.fontColor,
    titlePos: { x: 0, y: 0 },
    showLegend: true,
    legendPos: "t",
    legendFontSize: 8,
    legendColor: defaultOptions.fontColor,
    showValAxisTitle: true,
    valAxisLabelColor: defaultOptions.fontColor,
    valAxisLabelFontSize: 8,
    valAxisLineShow: false,
    valGridLine: { size: 1, color: "DDDDDD" },
    showLeaderLines: false,
    valAxisTitleColor: defaultOptions.fontColor,
    catAxisLabelFontSize: 8,
    catAxisLabelColor: defaultOptions.fontColor,
    catAxisLineShow: false,
    chartColors: defaultOptions.chartColors,
    lineSize: 1,
    lineDataSymbol: "none",
  };
}

export function createDoughnutChartSettings(
  defaultOptions: DefaultOptions,
): DoughnutChartSettings {
  return {
    showTitle: false,
    showPercent: true,
    showLegend: true,
    chartColors: defaultOptions.chartColors,
    legendPos: "t",
    legendFontSize: 8,
    dataLabelColor: "FFFFFF",
    dataLabelFontSize: 5,
    titleFontSize: 12,
    titleFontFace: "Helvetica Neue Bold",
    titleColor: defaultOptions.fontColor,
    titlePos: { x: 0, y: 0 },
  };
}

export const LINE_CHART_SETTINGS: LineChartSettings =
  createLineChartSettings(DEFAULT_OPTIONS);

export const DOUGHNUT_CHART_SETTINGS: DoughnutChartSettings =
  createDoughnutChartSettings(DEFAULT_OPTIONS);

export const PRESENTATION_SETTINGS: PresentationSettings = {
  subtitleFontSize: 13,
};

export const SLIDE_LAYOUT = {
  name: "LAYOUT_16x9",
  width: 10, // inches
  height: 5.625, // inches (10 * 9/16)
};

export const SLIDE_POSITIONS = {
  // Title area
  title: { x: 1, y: 0.6, w: 8, h: 0.4 },
  titleLine: { x: 1, y: 1.1, w: 8 },
  dateRange: { x: 6, y: 0.7, w: 3, h: 0.25 },

  // Footer area
  footer: { x: 1, y: 5.29, h: 0.15 },
  poweredBy: { x: 7.4, y: 5.29, h: 0.15 },
  logo: { x: 8.2, y: 5.29, w: 0.8, h: 0.1204 },
  footerLine: { x: 1, y: 5.2, w: 8 },

  // Header area
  pageNumber: { x: 8.5, y: 0.13, h: 0.15 },
  reportTitle: { x: 1, y: 0.13, h: 0.15 },

  // Content area (below title, above footer)
  content: { x: 1, y: 1.3, w: 8, h: 3.7 },
};

export const TEXT_STYLES = {
  slideTitle: {
    fontSize: 22,
    fontFace: "Helvetica Neue",
    color: "363636",
    bold: false,
  },
  sectionHeader: {
    fontSize: 11,
    fontFace: "Helvetica Neue",
    color: "363636",
    bold: true,
  },
  bodyText: {
    fontSize: 9,
    fontFace: "Helvetica Neue Light",
    color: "363636",
  },
  smallText: {
    fontSize: 8,
    fontFace: "Helvetica Neue",
    color: "363636",
  },
  accentText: {
    fontSize: 11,
    fontFace: "Helvetica Neue",
    color: "01827e",
  },
  footerText: {
    fontSize: 8,
    fontFace: "Helvetica Neue",
    color: "363636",
  },
  chartTitle: {
    fontSize: 11,
    fontFace: "Helvetica Neue",
    color: "363636",
    bold: true,
  },
  metricValue: {
    fontSize: 22,
    fontFace: "Helvetica Neue",
    color: "01827e",
    bold: false,
  },
  metricLabel: {
    fontSize: 10,
    fontFace: "Helvetica Neue",
    color: "363636",
    bold: true,
  },
};
